import { type Dispatch, type SetStateAction } from 'react';

import {
  COMMERCE_HUB_FIELD_TYPE_TO_FIELD_STATE,
  COMMERCE_HUB_FIELD_TYPE_TO_HUMAN_READABLE_NAME
} from 'payments/constants/commerceHub';
import { COMMERCE_HUB_PAYMENT_FORM_CUSTOMIZATIONS } from 'payments/constants/commerceHub';
import { CardFieldStates } from 'payments/types/card';
import { CommerceHubPaymentFormFromPromise } from 'payments/types/commerceHub';
import { getEnvironmentName } from 'utility/environment';

type CommerceHubFieldTypeKeys = 'cardNumber' | 'securityCode' | 'expiration';

type CommerceHubFormState = {
  cardBrand?: string;
  fields: {
    [key in CommerceHubFieldTypeKeys]: {
      hasData: boolean;
      isValid: boolean;
      isPotentiallyValid: boolean;
      shouldShowError: boolean;
    };
  };
};

export const _getCommerceHubEnv = () => {
  const environmentName = getEnvironmentName();

  return environmentName === 'production' ? 'PROD' : 'CERT';
};

export function initCommerceHub(
  setCardFieldStates: Dispatch<SetStateAction<CardFieldStates>>
): CommerceHubPaymentFormFromPromise {
  const onFocus = (field: CommerceHubFieldTypeKeys) => {
    const fieldStateKey = COMMERCE_HUB_FIELD_TYPE_TO_FIELD_STATE?.[
      field
    ] as keyof CardFieldStates;

    if (fieldStateKey) {
      setCardFieldStates((currentFieldStates: CardFieldStates) => ({
        ...currentFieldStates,
        [fieldStateKey]: {
          ...currentFieldStates?.[fieldStateKey],
          touched: true,
          isFocused: true
        }
      }));
    }
  };

  const onLostFocus = (
    field: CommerceHubFieldTypeKeys,
    formState: CommerceHubFormState
  ) => {
    const fieldStateKey = COMMERCE_HUB_FIELD_TYPE_TO_FIELD_STATE?.[
      field
    ] as keyof CardFieldStates;

    if (fieldStateKey) {
      setCardFieldStates((currentFieldStates: CardFieldStates) => {
        const newFieldState = {
          ...currentFieldStates[fieldStateKey],
          isFocused: false,
          isEmpty: !formState.fields[field].hasData
        };

        if (!formState.fields[field].isValid) {
          const humanReadableField =
            COMMERCE_HUB_FIELD_TYPE_TO_HUMAN_READABLE_NAME[field];
          if (formState.fields[field].hasData) {
            newFieldState.error = `${humanReadableField} is invalid`;
          } else {
            newFieldState.error = `${humanReadableField} is required`;
          }
        } else {
          newFieldState.error = null;
        }

        return {
          ...currentFieldStates,
          [fieldStateKey]: newFieldState
        };
      });
    }
  };

  return window?.fiserv?.commercehub?.createPaymentForm({
    data: {
      environment: _getCommerceHubEnv(),
      ...COMMERCE_HUB_PAYMENT_FORM_CUSTOMIZATIONS
    },

    hooks: { onFocus, onLostFocus }
  });
}
